import { css } from '@emotion/react'
import { HFlow, isEmpty, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { useAcompanhamentoPreNatalVacinacaoQuery } from 'graphql/hooks.generated'
import { TipoAcompanhamentoEnum } from 'graphql/types.generated'
import { Fragment } from 'react'

import { AcompanhamentoCard } from '../../components/AcompanhamentoCard'
import { AcompanhamentoHeaderLink } from '../../components/AcompanhamentoHeaderLink'
import { AcompanhamentoIconLabel } from '../../components/AcompanhamentoIconLabel'
import { ImunobiologicoSection } from './ImunobiologicoSection'

interface VacinacaoGestacaoCardProps {
  dataInicioGestacao: Date
  vacinacaoPath: string
  loading?: boolean
  prontuarioId: string
  isEscutaInicial: boolean
  tipoAcompanhamento: TipoAcompanhamentoEnum
  dataInicioPuerperio?: Date
}

export function VacinacaoGestacaoCard(props: Readonly<VacinacaoGestacaoCardProps>) {
  const {
    dataInicioGestacao,
    vacinacaoPath,
    loading,
    prontuarioId,
    isEscutaInicial,
    tipoAcompanhamento,
    dataInicioPuerperio,
  } = props
  const theme = useTheme()
  const styles = createStyles(theme)

  const { data, loading: vacinacaoLoading } = useAcompanhamentoPreNatalVacinacaoQuery({
    variables: {
      input: {
        prontuarioId: prontuarioId,
        dataInicioGestacao: dataInicioGestacao,
        dataInicioPuerperio: dataInicioPuerperio,
        tipoAcompanhamento,
      },
    },
    skip: !prontuarioId,
    fetchPolicy: 'cache-and-network',
  })

  const vacinasAplicadas = data?.acompanhamentoPreNatalVacinacao?.vacinasAplicadas || []
  const vacinasAprazadas = data?.acompanhamentoPreNatalVacinacao?.vacinasAprazadas || []
  const ultimaVacinaAplicadaCovid = data?.acompanhamentoPreNatalVacinacao?.ultimaVacinaAplicadaCovid
  const proximaVacinaAprazadaCovid = data?.acompanhamentoPreNatalVacinacao?.proximaVacinaAprazadaCovid

  const hasVacinasAprazadas = proximaVacinaAprazadaCovid || !isEmpty(vacinasAprazadas)

  return (
    <AcompanhamentoCard
      id='vacinacao'
      title='Vacinação'
      tipoAcompanhamento={tipoAcompanhamento}
      loading={loading || vacinacaoLoading}
      placeholder='Nenhuma vacinação registrada'
      headerContent={
        !isEscutaInicial ? <AcompanhamentoHeaderLink title='Visualizar calendário' path={vacinacaoPath} /> : undefined
      }
      justifyHeaderContent='space-between'
    >
      <VFlow vSpacing={0}>
        <Box style={styles.box}>
          <AcompanhamentoIconLabel icon='needleFilled' iconText='Última aplicação' />
        </Box>
        <HFlow hSpacing={0.5} style={styles.bodyContent}>
          {vacinasAplicadas.map((item, index) => (
            <ImunobiologicoSection key={index} registroVacinacao={item} />
          ))}
          {ultimaVacinaAplicadaCovid && <ImunobiologicoSection registroVacinacao={ultimaVacinaAplicadaCovid} isCovid />}
        </HFlow>
        <Box style={styles.box}>
          <AcompanhamentoIconLabel icon='calendarFilled' iconText='Aplicações aprazadas' />
        </Box>
        <HFlow style={styles.bodyContent}>
          {hasVacinasAprazadas ? (
            <Fragment>
              {!isEmpty(vacinasAprazadas) &&
                vacinasAprazadas?.map((item, index) => (
                  <ImunobiologicoSection key={index} registroVacinacao={item} isAprazada />
                ))}
              {proximaVacinaAprazadaCovid && (
                <ImunobiologicoSection registroVacinacao={proximaVacinaAprazadaCovid} isCovid isAprazada />
              )}
            </Fragment>
          ) : (
            <Text> Nenhuma vacina aprazada </Text>
          )}
        </HFlow>
      </VFlow>
    </AcompanhamentoCard>
  )
}

const createStyles = (theme: Theme) => ({
  bodyContent: css`
    padding: 0.5rem;
  `,
  box: css`
    margin: 0.5rem;
    padding: 0.5rem;
    border: 0;
    background: ${theme.pallete.gray.c90};
  `,
})
